import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserResponse } from 'algorilla-shared-libs';
import { User } from 'src/app/model/user';
import { AccessControlSet } from 'src/app/security/model/access-control-set';
import { AccessControlSetRequest } from 'src/app/security/model/access-control-set-request';
import { DashboardGroupAuthorization } from 'src/app/security/model/dashboard-group-authorization';
import { DashboardGroupAuthorizationRequest } from 'src/app/security/model/dashboard-group-authorization-request';
import { ExternalAuthenticationGroupMapping } from 'src/app/security/model/external-authentication-group-mapping';
import { ExternalAuthenticationGroupMappingRequest } from 'src/app/security/model/external-authentication-group-mapping-request';
import { Group } from 'src/app/security/model/group';
import { GroupRequest } from 'src/app/security/model/group-request';
import { LdapConfiguration } from 'src/app/security/model/ldap-configuration';
import { LdapSearchRequest } from 'src/app/security/model/ldap-search-request';
import { LdapSearchResponse } from 'src/app/security/model/ldap-search-response';
import { MaskingRule } from 'src/app/security/model/masking-rule';
import { MaskingRuleRequest } from 'src/app/security/model/masking-rule-request';
import { RadiusConfiguration } from 'src/app/security/model/radius-configuration';
import { SearchRule } from 'src/app/security/model/search-rule';
import { SearchRuleRequest } from 'src/app/security/model/search-rule-request';
import { TestMaskingRulesRequest } from 'src/app/security/model/TestMaskingRuleRequest';
import { TestMaskingRulesResponse } from 'src/app/security/model/TestMaskingRuleResponse';
import { UserRequest } from 'src/app/security/model/user-request';
import base32Encode from 'base32-encode';
import { BowmanCacheService } from 'bowman-cache';
import { ActionResult, HelperFunctions } from 'bowman-commons';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})

export class SecurityService {
  private _mainUrl: string = window.location.pathname + '/api/security/';
  private _uuid: string;
  headers: HttpHeaders = new HttpHeaders();
  constructor(private _http: HttpClient, private _bowmanCacheService: BowmanCacheService) {
    this.headers = new HttpHeaders().append('Content-Type', 'application/json').append('Accept', 'application/json');
    this._uuid = HelperFunctions.uuid();
    console.debug("Security service init: " + this._uuid);
  }
  authenticate(username: string, password: string): Observable<ActionResult<UserResponse>> {
    let utf8Encode = new TextEncoder();
    const loginCredentials = new HttpParams()
      .set('username', base32Encode(utf8Encode.encode(username), 'RFC4648'))
      .set('password', base32Encode(utf8Encode.encode(password), 'RFC4648'))
    /*       const loginCredentials = new HttpParams()
          .set('username', username)
          .set('password', password);
     */
    let headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json'
    });
    headers.set("Content-Type", "application/x-www-form-urlencoded");
    const dataUrl = HelperFunctions.cleanUrl(window.location.pathname + '/api/login');
    return this._http.post<ActionResult<UserResponse>>(dataUrl, loginCredentials, { headers }).pipe(catchError(this.handleError));
  }

  public isAuthenticated(): Observable<ActionResult<UserResponse>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'isAuthenticated');
    return this._http.get<ActionResult<UserResponse>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public logout(): Observable<string> {
    const dataUrl = HelperFunctions.cleanUrl(window.location.pathname + '/api/logout');
    return this._http.get<string>(dataUrl).pipe(catchError(this.handleError));
  }
  public isLdapActive(): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'isLdapActive');
    return this._http.get<ActionResult<boolean>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }
  public testLDAPConnection(url: string, username: string, password: string): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'testLDAPConnection/?ldapUrl=' + url + '&technicalUser=' + username + '&technicalPassword=' + password);
    return this._http.get<ActionResult<boolean>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public createLdapConfig(request: LdapConfiguration): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'createLdapConfig');
    this._bowmanCacheService.flushServiceCacheByMethod('getLdapConfig');
    return this._http.post<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public updateLdapConfig(request: LdapConfiguration): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'updateLdapConfig');
    this._bowmanCacheService.flushServiceCacheByMethod('getLdapConfig');
    return this._http.put<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public getLdapConfig(): Observable<ActionResult<LdapConfiguration>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'getLdapConfig');
    return this._http.get<ActionResult<LdapConfiguration>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public deleteLdapConfig(): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'deleteLdapConfig');
    this._bowmanCacheService.flushServiceCacheByMethod('getLdapConfig');
    return this._http.delete<ActionResult<boolean>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public searchLDAP(request: LdapSearchRequest): Observable<ActionResult<Array<LdapSearchResponse>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'searchLDAP');
    return this._http.post<ActionResult<Array<LdapSearchResponse>>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public createGroup(request: GroupRequest): Observable<ActionResult<string>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'group/create')
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    this._bowmanCacheService.flushServiceCacheByMethod('listUsers');
    this._bowmanCacheService.flushServiceCacheByMethod('listDashboardAuthorization');
    return this._http.post<ActionResult<string>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public updateGroup(id: string, request: GroupRequest): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'group/update/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    this._bowmanCacheService.flushServiceCacheByMethod('listUsers');
    this._bowmanCacheService.flushServiceCacheByMethod('listDashboardAuthorization');
    return this._http.put<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public deleteGroup(id: string): Observable<ActionResult<number>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'group/delete/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    this._bowmanCacheService.flushServiceCacheByMethod('listUsers');
    this._bowmanCacheService.flushServiceCacheByMethod('listDashboardAuthorization');
    return this._http.delete<ActionResult<number>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }
  public listGroups(): Observable<ActionResult<Array<Group>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'group/list')
    return this._http.get<ActionResult<Array<Group>>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public getGroupById(id: string): Observable<ActionResult<Group>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'group/getById')
    return this._http.post<ActionResult<Group>>(dataUrl, id, { headers: this.headers }).pipe(catchError(this.handleError));

  }


  public listAccessControlSet(): Observable<ActionResult<Array<AccessControlSet>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'accessControlSet/list');
    return this._http.get<ActionResult<Array<AccessControlSet>>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));

  }

  public getAccessControlSetById(id: string): Observable<ActionResult<AccessControlSet>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'accessControlSet/getById')
    return this._http.post<ActionResult<AccessControlSet>>(dataUrl, id, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public createAccessControlSet(request: AccessControlSetRequest): Observable<ActionResult<string>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'accessControlSet/create');
    this._bowmanCacheService.flushServiceCacheByMethod('listAccessControlSet');
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    return this._http.post<ActionResult<string>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public updateAccessControlSet(id: string, request: AccessControlSetRequest): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'accessControlSet/update/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('listAccessControlSet');
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    return this._http.put<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public deleteAccessControlSet(id: string): Observable<ActionResult<number>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'accessControlSet/delete/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('listAccessControlSet');
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    return this._http.delete<ActionResult<number>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));

  }

  public createSearchRule(request: SearchRuleRequest): Observable<ActionResult<string>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'searchRule/create');
    this._bowmanCacheService.flushServiceCacheByMethod('listSearchRules');
    this._bowmanCacheService.flushServiceCacheByMethod('listAccessControlSet');
    return this._http.post<ActionResult<string>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public updateSearchRule(id: string, request: SearchRuleRequest): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'searchRule/update/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('listSearchRules');
    this._bowmanCacheService.flushServiceCacheByMethod('listAccessControlSet');
    return this._http.put<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }

  public listSearchRules(): Observable<ActionResult<Array<SearchRule>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'searchRule/list')
    return this._http.get<ActionResult<Array<SearchRule>>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  public deleteSearchRule(id: string): Observable<ActionResult<number>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'searchRule/delete/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('listSearchRules');
    this._bowmanCacheService.flushServiceCacheByMethod('listAccessControlSet');
    return this._http.delete<ActionResult<number>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public getSearchRuleById(id: string): Observable<ActionResult<SearchRule>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'searchRule/getById');
    return this._http.post<ActionResult<SearchRule>>(dataUrl, id, { headers: this.headers }).pipe(catchError(this.handleError));

  }

  public testMaskingRule(request: TestMaskingRulesRequest): Observable<ActionResult<TestMaskingRulesResponse>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'maskingRule/test');
    return this._http.post<ActionResult<TestMaskingRulesResponse>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }

  public createMaskingRule(request: MaskingRuleRequest): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'maskingRule/create');
    this._bowmanCacheService.flushServiceCacheByMethod('listMaskingRules');
    this._bowmanCacheService.flushServiceCacheByMethod('listAccessControlSet');
    return this._http.post<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public updateMaskingRule(id: string, request: MaskingRuleRequest): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'maskingRule/update/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('listMaskingRules');
    this._bowmanCacheService.flushServiceCacheByMethod('listAccessControlSet');
    return this._http.put<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public listMaskingRules(): Observable<ActionResult<Array<MaskingRule>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'maskingRule/list')
    return this._http.get<ActionResult<Array<MaskingRule>>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }
  public deleteMaskingRule(id: string): Observable<ActionResult<number>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'maskingRule/delete/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('listMaskingRules');
    this._bowmanCacheService.flushServiceCacheByMethod('listAccessControlSet');
    return this._http.delete<ActionResult<number>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public getMaskingRuleById(id: string): Observable<ActionResult<MaskingRule>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'maskingRule/getById')
    return this._http.post<ActionResult<MaskingRule>>(dataUrl, id, { headers: this.headers }).pipe(catchError(this.handleError));

  }



  public createUser(request: UserRequest): Observable<ActionResult<string>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'user/create');
    this._bowmanCacheService.flushServiceCacheByMethod('listUsers');
    return this._http.post<ActionResult<string>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public updateUser(id: string, request: UserRequest): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'user/update/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('listUsers');
    return this._http.put<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public updateUserOptions(request: any): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'user/updateOptions/');
    this._bowmanCacheService.flushServiceCacheByMethod('listUsers');
    return this._http.put<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public listUsers(): Observable<ActionResult<Array<User>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'user/list');
    return this._http.get<ActionResult<Array<User>>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));

  }
  public getUserById(id: string): Observable<ActionResult<User>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'user/getById')
    return this._http.post<ActionResult<User>>(dataUrl, id, { headers: this.headers }).pipe(catchError(this.handleError));

  }

  public deleteUser(id: string): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'user/delete/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('listUsers');
    return this._http.delete<ActionResult<boolean>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));

  }

  listDashboardAuthorization(): Observable<ActionResult<Array<DashboardGroupAuthorization>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'dashboardAuthorization/list');
    return this._http.get<ActionResult<Array<DashboardGroupAuthorization>>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  createDashboardAuthorization(dashRoleAuth: DashboardGroupAuthorizationRequest): Observable<boolean> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'dashboardAuthorization/create');
    this._bowmanCacheService.flushServiceCacheByMethod('listDashboardAuthorization');
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    return this._http.post<boolean>(dataUrl, dashRoleAuth, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  deleteDashboardAuthorization(groupId: string, dashId: string): Observable<Boolean> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'dashboardAuthorization/delete/' + groupId + '/' + dashId);
    this._bowmanCacheService.flushServiceCacheByMethod('listDashboardAuthorization');
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    return this._http.delete<Boolean>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  createDashboardAuthorizationByList(dashRoleAuthList: Array<DashboardGroupAuthorization>,): Observable<ActionResult<Array<Map<string, string>>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'dashboardAuthorization/createByList');
    this._bowmanCacheService.flushServiceCacheByMethod('listDashboardAuthorization');
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    return this._http.post<ActionResult<Array<Map<string, string>>>>(dataUrl, dashRoleAuthList, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  /*
  updateDashboardAuthorizationByList(dashRoleAuthList: Array<DashboardGroupAuthorizationRequest>): Observable<ActionResult<Array<Map<string, string>>>> {
    const dataUrl = this._mainUrl + 'dashboardAuthorization/updateByList';
    this._bowmanCacheService.flushServiceCacheByMethod( 'listDashboardAuthorization');
    return this._http.post<ActionResult<Array<Map<string, string>>>>(dataUrl, dashRoleAuthList, { headers: this.headers }).pipe(catchError(this.handleError));
  }
  */

  updateDashboardAuthorizationByListAndDashboardId(dashRoleAuthList: DashboardGroupAuthorizationRequest, dashboardId: string): Observable<ActionResult<Array<Map<string, string>>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'dashboardAuthorization/updateByListAndDashboardId');
    this._bowmanCacheService.flushServiceCacheByMethod('listDashboardAuthorization');
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    return this._http.post<ActionResult<Array<Map<string, string>>>>(dataUrl, dashRoleAuthList, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  updateDashboardAuthorizationByListAndGroupId(dashRoleAuthList: DashboardGroupAuthorizationRequest, groupId: string): Observable<ActionResult<Array<Map<string, string>>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'dashboardAuthorization/updateByListAndGroupId');
    this._bowmanCacheService.flushServiceCacheByMethod('listDashboardAuthorization');
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    return this._http.post<ActionResult<Array<Map<string, string>>>>(dataUrl, dashRoleAuthList, { headers: this.headers }).pipe(catchError(this.handleError));
  }


  deleteDashboardAuthorizationByList(idsMap: Array<{ [fieldName: string]: string }>): Observable<ActionResult<Boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'dashboardAuthorization/deleteByList');
    this._bowmanCacheService.flushServiceCacheByMethod('listDashboardAuthorization');
    this._bowmanCacheService.flushServiceCacheByMethod('listGroups');
    return this._http.post<ActionResult<Boolean>>(dataUrl, idsMap, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  createExternalAuthenticationGroupMapping(req: Array<ExternalAuthenticationGroupMappingRequest>): Observable<ActionResult<Array<String>>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'externalAuthenticationGroupMapping/create');
    this._bowmanCacheService.flushServiceCacheByMethod('getLdapConfig');
    this._bowmanCacheService.flushServiceCacheByMethod('listExternalAuthenticationGroupMapping');
    return this._http.post<ActionResult<Array<String>>>(dataUrl, req, { headers: this.headers }).pipe(catchError(this.handleError));
  }


  listExternalAuthenticationGroupMapping(): Observable<ActionResult<ExternalAuthenticationGroupMapping[]>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'externalAuthenticationGroupMapping/list');
    return this._http.get<ActionResult<ExternalAuthenticationGroupMapping[]>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  updateExternalAuthenticationGroupMapping(req: Array<ExternalAuthenticationGroupMappingRequest>): Observable<boolean> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'externalAuthenticationGroupMapping/update');
    this._bowmanCacheService.flushServiceCacheByMethod('getLdapConfig');
    this._bowmanCacheService.flushServiceCacheByMethod('listExternalAuthenticationGroupMapping');
    return this._http.put<boolean>(dataUrl, req, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  deleteExternalAuthenticationGroupMapping(id: string): Observable<boolean> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'externalAuthenticationGroupMapping/delete/' + id);
    this._bowmanCacheService.flushServiceCacheByMethod('getLdapConfig');
    this._bowmanCacheService.flushServiceCacheByMethod('listExternalAuthenticationGroupMapping');
    return this._http.delete<boolean>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  createRadiusConfig(request: RadiusConfiguration): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'createRadiusConfig');
    this._bowmanCacheService.flushServiceCacheByMethod('getRadiusConfig');
    return this._http.post<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError));
  }

  updateRadiusConfig(request: RadiusConfiguration): Observable<ActionResult<boolean>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'updateRadiusConfig');
    this._bowmanCacheService.flushServiceCacheByMethod('getRadiusConfig');
    return this._http.put<ActionResult<boolean>>(dataUrl, request, { headers: this.headers }).pipe(catchError(this.handleError))

  }

  getRadiusConfig(): Observable<ActionResult<RadiusConfiguration>> {
    const dataUrl = HelperFunctions.cleanUrl(this._mainUrl + 'getRadiusConfig');
    return this._http.get<ActionResult<RadiusConfiguration>>(dataUrl, { headers: this.headers }).pipe(catchError(this.handleError))
  }



  private handleError(error: any) {
    let parsedError: any;
    if (error && error.error && error.error.message) {
      parsedError = error.error.message;
    } else if (error && error.message) {
      parsedError = error.message;
    } else if (error && error._body) {
      parsedError = error._body;
    } else {
      parsedError = 'Security Manager Service Error';
    }
    return observableThrowError(parsedError);
  }
}
